import { useEffect } from 'react';
import Widget from 'components/Dashboard/Widget';
import { useKPIGetWorkedTimePerFieldTesterDay } from 'api/kpi/kpi';
import { KPIsWidgetEnum, LongTitles } from '../../utils';
import useOperationsKpiFilter from './useOperationsKpiFilter';
import {
  ControlFigures,
  hm,
  nf,
  tableStyle,
  tdNumStyle,
  tdStyle,
  thNumStyle,
  thStyle,
  totalRowStyle,
  WidgetState,
} from '../management/shared';

/**
 * Arbetad tid delat på antal FT-dagar, per FT:s hemlän enligt hemankaret. Se
 * OperationsKpiLogic.GetWorkedTimePerFieldTesterDay.
 */
const WorkedTimePerFieldTesterDayWidget = () => {
  const { isReady, isDateRangeTooLong, filter, deps } =
    useOperationsKpiFilter();
  const getData = useKPIGetWorkedTimePerFieldTesterDay();

  useEffect(() => {
    if (isReady) {
      getData.mutate({ data: filter });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  const result = getData.data;

  return (
    <Widget
      title={LongTitles[KPIsWidgetEnum.WorkedTimePerFieldTesterDay]}
      count={result?.fieldTesters}
      isLoading={getData.isLoading}
      info="Arbetad tid (start till slut minus rast, persontransport inräknad, överlappande uppdrag räknas en gång) för slutförda och godkända uppdrag, delat på antal FT-dagar — dagar då fälttestaren arbetade. Hemlän = länet för fälttestarens första hemankare. Bara period och länsfilter används."
    >
      <WidgetState
        isLoading={getData.isLoading}
        isDateRangeTooLong={isDateRangeTooLong}
        isEmpty={!result || result.fieldTesterDays === 0}
      >
        <p style={{ fontSize: '15px', margin: '4px 0 12px' }}>
          <strong>{hm(result?.averageHoursPerFieldTesterDay)}</strong> i snitt
          per FT-dag · {nf(result?.fieldTesters ?? 0)} FT ·{' '}
          {nf(result?.fieldTesterDays ?? 0)} FT-dagar
        </p>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>Hemlän (hemankare)</th>
              <th style={thNumStyle}>FT</th>
              <th style={thNumStyle}>FT-dagar</th>
              <th style={thNumStyle}>Arbetade timmar</th>
              <th style={thNumStyle}>Snitt per FT-dag</th>
            </tr>
          </thead>
          <tbody>
            {(result?.byCounty ?? []).map((row) => (
              <tr
                key={row.countyName}
                style={{ borderBottom: '1px solid #eee' }}
              >
                <td style={tdStyle}>{row.countyName}</td>
                <td style={tdNumStyle}>{nf(row.fieldTesters)}</td>
                <td style={tdNumStyle}>{nf(row.fieldTesterDays)}</td>
                <td style={tdNumStyle}>{nf(row.workedHours, 1)}</td>
                <td style={tdNumStyle}>
                  {hm(row.averageHoursPerFieldTesterDay)}
                </td>
              </tr>
            ))}
            {result && (
              <tr style={totalRowStyle}>
                <td style={tdStyle}>Totalt</td>
                <td style={tdNumStyle}>{nf(result.fieldTesters)}</td>
                <td style={tdNumStyle}>{nf(result.fieldTesterDays)}</td>
                <td style={tdNumStyle}>{nf(result.workedHours, 1)}</td>
                <td style={tdNumStyle}>
                  {hm(result.averageHoursPerFieldTesterDay)}
                </td>
              </tr>
            )}
          </tbody>
        </table>
        <ControlFigures
          items={[
            result?.fieldTestersWithAnchorsInSeveralCounties
              ? `${nf(result.fieldTestersWithAnchorsInSeveralCounties)} FT har hemankare i flera län och räknas på det första`
              : null,
          ]}
        />
      </WidgetState>
    </Widget>
  );
};

export default WorkedTimePerFieldTesterDayWidget;
